import { ChangeEvent, useEffect, useMemo, useReducer, useState } from 'react';

import {
  AutoComplete,
  AutoCompleteOption,
  ChakraBox,
  ChakraHStack,
  ChakraMenu,
  ChakraMenuButton,
  ChakraMenuList,
  ChakraText,
  Checkbox,
  Close,
  DateFormat,
  DatePicker,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
  isDateRangeValid,
  Search,
  TextInput,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import DiagnosisSearch from '~/components/common/DiagnosisSearch';
import { HEADER_HEIGHT } from '~/lib/constants';
import {
  EpisodeTreatmentProgram,
  PatientListFilterReducerActions as FilterActions,
  PatientListFiltersState,
} from '~/lib/constants/patients.constants';
import { UNASSIGNED_FILTER_OPTION } from '~/lib/constants/user.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { toAutoCompleteOption } from '~/lib/util/menu';
import { getInitialFilterValue } from '~/lib/util/patient.util';
import {
  getProviderAutocompleteOption,
  getUserOption,
} from '~/lib/util/user.util';
import { userOptionAlphabeticalSortCompareFunction } from '~/lib/utils';
import {
  useGetAdminEpisodeStatusReasonsEnumQuery,
  useGetInsuranceVerificationStatusesEnumQuery,
  useGetMedicalClearanceStatusesEnumQuery,
  useGetPayorProgramsEnumQuery,
  useGetPayorsEnumQuery,
  useGetPsychClearanceStatusesEnumQuery,
  useGetTreatmentEpisodeLevelsEnumQuery,
  useGetUniversalStatusesEnumQuery,
} from '~/store/services/enumerations';
import {
  useGetAllProvidersV2Query,
  useLazyGetAllProvidersV2Query,
} from '~/store/services/providers';
import { useTreatmentPrograms } from '~/lib/hooks/useTreatmentPrograms';

export type PatientFiltersAction = {
  type: FilterActions;
  value?:
    | boolean
    | string
    | string[]
    | PatientListFiltersType
    | PatientFilterType
    | PatientFilterType[];
};

export type PatientFiltersReducer = (
  state: PatientListFiltersState,
  action: PatientFiltersAction,
) => PatientListFiltersState;

interface PatientFiltersProps {
  selectedFilter: PatientListFiltersType;
  onFilterChange: (val: PatientListFiltersType) => void;
}

const filterReducer = (
  state: PatientListFiltersState,
  action: PatientFiltersAction,
): PatientListFiltersState => {
  const { type, value } = action;
  switch (type) {
    case FilterActions.UPDATE_NAME:
      return {
        ...state,
        name: value as string,
      };
    case FilterActions.UPDATE_DIAGNOSIS:
      return {
        ...state,
        primaryDiagnosis: value as PatientFilterType[],
      };
    case FilterActions.UPDATE_EPISODE_STATUS:
      return {
        ...state,
        episodeStatus: value as PatientFilterType[],
      };
    case FilterActions.UPDATE_IS_PATIENT_PAUSED:
      return {
        ...state,
        isPatientPaused: value as boolean,
      };
    case FilterActions.UPDATE_ADMIT_DATE_FROM:
      return {
        ...state,
        admitDateFrom: value as PatientFilterType,
      };
    case FilterActions.UPDATE_ADMIT_DATE_TO:
      return {
        ...state,
        admitDateTo: value as PatientFilterType,
      };
    case FilterActions.UPDATE_CARE_TEAM_MEMBER:
      return {
        ...state,
        careTeamMember: value as PatientFilterType[],
      };
    case FilterActions.UPDATE_INSURANCE_VERIFICATION_STATUS:
      return {
        ...state,
        insuranceVerificationStatus: value as string[],
      };
    case FilterActions.UPDATE_MEDICAL_CLEARANCE_STATUS:
      return {
        ...state,
        medicalClearanceStatus: value as string[],
      };
    case FilterActions.UPDATE_PAYOR:
      return {
        ...state,
        payor: value as string[],
      };
    case FilterActions.UPDATE_PAYOR_PROGRAM:
      return {
        ...state,
        payorProgram: value as string[],
      };
    case FilterActions.UPDATE_ALL_FILTER:
      return { ...(action.value as PatientListFiltersType) };
    case FilterActions.UPDATE_EPISODE_LEVEL:
      return {
        ...state,
        episodeLevels: value as string[],
      };
    case FilterActions.UPDATE_TREATMENT_PROGRAM:
      return {
        ...state,
        treatmentPrograms: value as string[],
      };
    case FilterActions.UPDATE_PSYCH_CLEARANCE_STATUSES:
      return {
        ...state,
        psychClearanceStatuses: value as string[],
      };
    case FilterActions.UPDATE_EPISODE_STATUS_REASON:
      return {
        ...state,
        episodeStatusReasons: value as string[],
      };
    case FilterActions.UPDATE_UNIVERSAL_STATUS:
      return {
        ...state,
        universalStatuses: value as PatientFilterType[],
      };
    default:
      throw new Error();
  }
};

const GET_PROVIDERS_PAGE_SIZE = 15 as const;

const { primary } = EquipUIFireflyV2Theme.colors;

const TOP_LEVEL_ID = 'patient-filters';

const PatientFilters = ({
  onFilterChange,
  selectedFilter,
}: PatientFiltersProps) => {
  const { currentUser } = useGlobalContext();
  const [resetFilter, setResetFilter] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filterState, dispatch] = useReducer<PatientFiltersReducer>(
    filterReducer,
    getInitialFilterValue(currentUser),
  );

  const {
    data: adminEpisodeLevels,
    isLoading: isGetAdminEpisodeLevelsLoading,
  } = useGetTreatmentEpisodeLevelsEnumQuery();

  const { data: payors, isLoading: isGetPayorsLoading } =
    useGetPayorsEnumQuery();
  const {
    allPrograms: episodeTreatmentPrograms,
    isLoading: isGetEpisodeTreatmentProgramsLoading,
  } = useTreatmentPrograms();
  const { data: payorPrograms, isLoading: isGetPayorProgramsLoading } =
    useGetPayorProgramsEnumQuery();

  const {
    data: insuranceVerificationStatuses,
    isLoading: isGetInsuranceVerificationStatusesLoading,
  } = useGetInsuranceVerificationStatusesEnumQuery();

  const {
    data: medicalClearanceStatuses,
    isLoading: isGetMedicalClearanceStatusesLoading,
  } = useGetMedicalClearanceStatusesEnumQuery();
  const {
    data: psychClearanceStatuses,
    isLoading: isGetPsychClearanceStatusesLoading,
  } = useGetPsychClearanceStatusesEnumQuery();
  const {
    data: adminEpisodeStatusReasons,
    isLoading: isGetAdminEpisodeStatusReasonsLoading,
  } = useGetAdminEpisodeStatusReasonsEnumQuery();

  const payorOptions: AutoCompleteOption[] = useMemo<AutoCompleteOption[]>(
    () =>
      (payors ?? []).map(({ label }: Payor) => ({
        primaryText: label,
        value: label,
      })),
    [payors],
  );

  const payorProgramOptions: AutoCompleteOption[] = useMemo<
    AutoCompleteOption[]
  >(
    () =>
      [...new Set((payorPrograms ?? []).map(({ label }) => label))].map(
        (name: string) => ({ primaryText: name, value: name }),
      ),
    [payorPrograms],
  );

  const {
    data: initialProviderData,
    isFetching: isGetInitialProvidersFetching,
  } = useGetAllProvidersV2Query(
    {
      'page-size': GET_PROVIDERS_PAGE_SIZE,
      'page-number': 1,
    },
    { skip: !isOpen },
  );

  const [
    getProviders,
    { data: providerTeamMembers, isFetching: isGetProviderTeamMembersFetching },
  ] = useLazyGetAllProvidersV2Query();

  const {
    data: universalStatusesRaw,
    isLoading: isGetUniversalStatusesLoading,
  } = useGetUniversalStatusesEnumQuery();

  const universalStatusOptions = useMemo<AutoCompleteOption[]>(() => {
    return (universalStatusesRaw ?? []).map(({ description, code }) => ({
      primaryText: description,
      value: code,
    }));
  }, [universalStatusesRaw]);

  const handleChangeNameFilter = (
    e?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { value } = e?.target ?? {};
    dispatch({
      type: FilterActions.UPDATE_NAME,
      value: value || '',
    });
  };

  const selfOption = currentUser
    ? {
        ...getUserOption(true)(currentUser),
        isMe: true,
      }
    : null;

  const providerOptions: AutoCompleteOption[] = useMemo(() => {
    const providers =
      providerTeamMembers?.data || initialProviderData?.data || [];
    return [
      UNASSIGNED_FILTER_OPTION,
      ...(selfOption ? [selfOption] : []),
      ...providers
        .filter(({ userId }) => userId !== currentUser.userId)
        .map((provider) => getProviderAutocompleteOption(provider))
        .sort(userOptionAlphabeticalSortCompareFunction),
    ];
  }, [providerTeamMembers, initialProviderData, currentUser.userId]);

  const patientNameSearchValue = filterState?.name ?? '';
  const searchIcon = (
    <Search fill={primary[100]} fillOpacity={1} height="20px" width="20px" />
  );

  const handleAdmitDateFromSelect = (date: Date): void => {
    if (!date) return;
    dispatch({
      type: FilterActions.UPDATE_ADMIT_DATE_FROM,
      value: {
        displayText: `Start: ${DateTime.fromJSDate(date).toFormat(DateFormat.longDate)}`,
        value: DateTime.fromJSDate(date).toFormat(DateFormat.shortYearMonthDay),
      },
    });
  };

  const handleAdmitDateToSelect = (date: Date): void => {
    if (!date) return;
    dispatch({
      type: FilterActions.UPDATE_ADMIT_DATE_TO,
      value: {
        displayText: `End: ${DateTime.fromJSDate(date).toFormat(DateFormat.longDate)}`,
        value: DateTime.fromJSDate(date).toFormat(DateFormat.shortYearMonthDay),
      },
    });
  };

  // If "admitDateTo" filter is now invalid, update it
  useEffect(() => {
    const { admitDateFrom, admitDateTo } = filterState ?? {};
    if (
      admitDateFrom &&
      admitDateTo &&
      !isDateRangeValid(admitDateFrom?.value, admitDateTo?.value)
    ) {
      handleAdmitDateToSelect(
        DateTime.fromFormat(
          admitDateFrom?.value,
          DateFormat.shortYearMonthDay,
        ).toJSDate(),
      );
    }
  }, [filterState]);

  // If "admitDateFrom" filter is now invalid, update it
  useEffect(() => {
    const { admitDateFrom, admitDateTo } = filterState ?? {};

    if (
      admitDateFrom &&
      admitDateTo &&
      !isDateRangeValid(admitDateFrom?.value, admitDateTo?.value)
    ) {
      handleAdmitDateFromSelect(
        DateTime.fromFormat(
          admitDateTo?.value,
          DateFormat.shortYearMonthDay,
        ).toJSDate(),
      );
    }
  }, [filterState]);

  const insuranceVerificationStatusOptions = useMemo<AutoCompleteOption[]>(
    () =>
      insuranceVerificationStatuses
        ?.map((status) => status.label)
        ?.map(toAutoCompleteOption) ?? [],
    [insuranceVerificationStatuses],
  );

  const medicalClearanceStatusOptions = useMemo<AutoCompleteOption[]>(
    () =>
      medicalClearanceStatuses
        ?.map((status) => status.label)
        ?.map(toAutoCompleteOption) ?? [],
    [medicalClearanceStatuses],
  );

  const episodeLevelsOptions = useMemo<AutoCompleteOption[]>(
    () =>
      adminEpisodeLevels
        ?.map((level) => level.label)
        .map(toAutoCompleteOption) ?? [],
    [adminEpisodeLevels],
  );

  const episodeTreatmentProgramsOptions = useMemo<AutoCompleteOption[]>(
    () => episodeTreatmentPrograms?.map(toAutoCompleteOption) ?? [],
    [episodeTreatmentPrograms],
  );

  const psychClearanceStatusesOptions = useMemo<AutoCompleteOption[]>(
    () =>
      psychClearanceStatuses
        ?.map((status) => status.label)
        ?.map(toAutoCompleteOption) ?? [],
    [psychClearanceStatuses],
  );

  const episodeClosedReasonsOption = useMemo<AutoCompleteOption[]>(
    () =>
      adminEpisodeStatusReasons
        ?.filter((sr) => sr.status === 'Closed')
        ?.map(({ label }) => label)
        ?.map(toAutoCompleteOption) ?? [],
    [adminEpisodeStatusReasons],
  );

  return (
    <ChakraMenu
      closeOnBlur
      id={`${TOP_LEVEL_ID}__menu`}
      placement="bottom-end"
      onClose={() => {
        setResetFilter(true);
        setIsOpen(false);
      }}
      onOpen={() => {
        setIsOpen(true);
        if (resetFilter) {
          dispatch({
            type: FilterActions.UPDATE_ALL_FILTER,
            value: selectedFilter,
          });
          setResetFilter(false);
        }
      }}
    >
      {({ onClose }) => (
        <>
          <ChakraMenuButton
            as={FireflyButton}
            id={`${TOP_LEVEL_ID}__menu-button`}
            leftIcon={FAIconName.plusRegular}
            size="small"
            variant="secondary"
          >
            Filter
          </ChakraMenuButton>
          <ChakraMenuList
            id={`${TOP_LEVEL_ID}__menu-list`}
            maxWidth="500px"
            padding="20px"
            width="500px"
            zIndex={100}
          >
            <ChakraBox
              maxHeight={`calc(100vh - ${HEADER_HEIGHT} - 220px)`}
              overflowY="scroll"
              paddingY="20px"
            >
              <AutoComplete
                endIcon={searchIcon}
                fetchOptions={(searchTerm) => {
                  getProviders({
                    'page-number': 1,
                    'page-size': GET_PROVIDERS_PAGE_SIZE,
                    'provider-search': searchTerm,
                    'include-email-search': false,
                  });
                }}
                id={`${TOP_LEVEL_ID}__care-team-member`}
                isDebounced
                isLoading={
                  isGetInitialProvidersFetching ||
                  isGetProviderTeamMembersFetching
                }
                marginBottom="16px"
                multiple
                numOptionsNeverFilterOut={2}
                onRemove={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_CARE_TEAM_MEMBER,
                    value: filterState.careTeamMember.filter(
                      ({ value: val }) => val !== value,
                    ),
                  });
                }}
                onSelect={({ value, primaryText }) => {
                  dispatch({
                    type: FilterActions.UPDATE_CARE_TEAM_MEMBER,
                    value: filterState?.careTeamMember?.concat({
                      displayText: primaryText,
                      value,
                    }),
                  });
                }}
                options={providerOptions}
                placeholder="Search"
                selected={filterState?.careTeamMember?.map(
                  ({ displayText, value }) => ({
                    primaryText: displayText,
                    value,
                  }),
                )}
                title="Provider team member"
                toggleListOnFocus
                useGenericIconWhen={(option: AutoCompleteOption) =>
                  option.value === UNASSIGNED_FILTER_OPTION.value
                }
              />

              <ChakraBox marginBottom="16px">
                <TextInput
                  autoComplete="off"
                  endIcon={
                    patientNameSearchValue && (
                      <Close
                        cursor="pointer"
                        fill={primary[100]}
                        height="16px"
                        width="16px"
                      />
                    )
                  }
                  endIconClick={handleChangeNameFilter}
                  id={`${TOP_LEVEL_ID}__name`}
                  label="Name"
                  onChange={handleChangeNameFilter}
                  placeholder="Search"
                  value={patientNameSearchValue}
                />
              </ChakraBox>

              <DiagnosisSearch
                id={TOP_LEVEL_ID}
                marginBottom="16px"
                multiple
                onRemove={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_DIAGNOSIS,
                    value: filterState.primaryDiagnosis.filter(
                      ({ value: val }) => val !== value,
                    ),
                  });
                }}
                placeholder="Search"
                selected={filterState.primaryDiagnosis.map(
                  ({ displayText, value }) => ({
                    primaryText: displayText,
                    value,
                  }),
                )}
                title="Primary diagnosis"
                updateSelectedDiagnosis={(
                  patientDiagnosis: PatientDiagnosis,
                ) => {
                  const { code, shortDescription }: DiagnosisCode =
                    patientDiagnosis?.diagnosis ?? null;
                  dispatch({
                    type: FilterActions.UPDATE_DIAGNOSIS,
                    value: filterState?.primaryDiagnosis?.concat({
                      displayText: shortDescription,
                      value: code,
                    }),
                  });
                }}
              />

              <AutoComplete
                endIcon={searchIcon}
                hideAvatar
                id={`${TOP_LEVEL_ID}__universal-status`}
                isLoading={isGetUniversalStatusesLoading}
                isTextInput
                marginBottom="16px"
                multiple
                onRemove={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_UNIVERSAL_STATUS,
                    value: filterState.universalStatuses.filter(
                      ({ value: val }) => val !== value,
                    ),
                  });
                }}
                onSelect={({ value, primaryText }) => {
                  dispatch({
                    type: FilterActions.UPDATE_UNIVERSAL_STATUS,
                    value: filterState.universalStatuses.concat({
                      displayText: primaryText,
                      value,
                    }),
                  });
                }}
                options={universalStatusOptions}
                placeholder="Search"
                selected={filterState.universalStatuses?.map(
                  ({ displayText, value }) => ({
                    primaryText: displayText,
                    value,
                  }),
                )}
                title="Patient status"
                toggleListOnFocus
              />

              <ChakraBox margin="24px 0 24px 8px">
                <Checkbox
                  id={`${TOP_LEVEL_ID}__patient-paused`}
                  isChecked={filterState.isPatientPaused}
                  label="Patient is paused"
                  marginBottom={4}
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    dispatch({
                      type: FilterActions.UPDATE_IS_PATIENT_PAUSED,
                      value: event.target.checked,
                    })
                  }
                  size="md"
                />
              </ChakraBox>
              <ChakraBox marginBottom="16px">
                <ChakraText
                  color="neutral.secondary"
                  fontSize="12px"
                  id={`${TOP_LEVEL_ID}__admit-date__label`}
                  margin="0 0 6px 14px"
                >
                  Admit date
                </ChakraText>

                <ChakraHStack spacing="10px">
                  <DatePicker
                    id={`${TOP_LEVEL_ID}__admit-date-start`}
                    onSelect={handleAdmitDateFromSelect}
                    placeHolderText="MM/DD/YYYY"
                    value={DateTime.fromFormat(
                      filterState.admitDateFrom?.value ?? '',
                      DateFormat.shortYearMonthDay,
                    ).toJSDate()}
                  />

                  <DatePicker
                    id={`${TOP_LEVEL_ID}__admit-date-end`}
                    onSelect={handleAdmitDateToSelect}
                    placeHolderText="MM/DD/YYYY"
                    value={DateTime.fromFormat(
                      filterState.admitDateTo?.value ?? '',
                      DateFormat.shortYearMonthDay,
                    ).toJSDate()}
                  />
                </ChakraHStack>
              </ChakraBox>

              <AutoComplete
                endIcon={searchIcon}
                hideAvatar
                id={`${TOP_LEVEL_ID}__payor`}
                isLoading={isGetPayorsLoading}
                isTextInput
                marginBottom="16px"
                multiple
                onRemove={({ value }: AutoCompleteOption) => {
                  dispatch({
                    type: FilterActions.UPDATE_PAYOR,
                    value: filterState.payor.filter(
                      (val: string) => val !== value,
                    ),
                  });
                }}
                onSelect={({ value }: AutoCompleteOption) => {
                  dispatch({
                    type: FilterActions.UPDATE_PAYOR,
                    value: filterState?.payor?.concat(value),
                  });
                }}
                options={payorOptions}
                placeholder="Search"
                selected={filterState?.payor?.map((value: string) => ({
                  primaryText: value,
                  value,
                }))}
                title="Insurance company"
                toggleListOnFocus
              />

              <AutoComplete
                endIcon={searchIcon}
                hideAvatar
                id={`${TOP_LEVEL_ID}__payor-program`}
                isLoading={isGetPayorProgramsLoading}
                isTextInput
                marginBottom="16px"
                multiple
                onRemove={({ value }: AutoCompleteOption) => {
                  dispatch({
                    type: FilterActions.UPDATE_PAYOR_PROGRAM,
                    value: filterState.payorProgram.filter(
                      (val: string) => val !== value,
                    ),
                  });
                }}
                onSelect={({ value }: AutoCompleteOption) => {
                  dispatch({
                    type: FilterActions.UPDATE_PAYOR_PROGRAM,
                    value: filterState?.payorProgram?.concat(value),
                  });
                }}
                options={payorProgramOptions}
                placeholder="Search"
                selected={filterState?.payorProgram?.map((value: string) => ({
                  primaryText: value,
                  value,
                }))}
                title="Payor program"
                toggleListOnFocus
              />

              <AutoComplete
                endIcon={searchIcon}
                hideAvatar
                id={`${TOP_LEVEL_ID}__medical-clearance-status`}
                isLoading={isGetMedicalClearanceStatusesLoading}
                isTextInput
                marginBottom="16px"
                multiple
                onRemove={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_MEDICAL_CLEARANCE_STATUS,
                    value: filterState.medicalClearanceStatus.filter(
                      (status) => status !== value,
                    ),
                  });
                }}
                onSelect={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_MEDICAL_CLEARANCE_STATUS,
                    value: filterState.medicalClearanceStatus.concat(value),
                  });
                }}
                options={medicalClearanceStatusOptions}
                placeholder="Search"
                selected={filterState.medicalClearanceStatus.map((val) => ({
                  primaryText: val,
                  value: val,
                }))}
                title="Medical clearance status"
                toggleListOnFocus
              />

              <AutoComplete
                endIcon={searchIcon}
                hideAvatar
                id={`${TOP_LEVEL_ID}__insurance-status`}
                isLoading={isGetInsuranceVerificationStatusesLoading}
                isTextInput
                marginBottom="16px"
                multiple
                onRemove={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_INSURANCE_VERIFICATION_STATUS,
                    value: filterState.insuranceVerificationStatus.filter(
                      (status) => status !== value,
                    ),
                  });
                }}
                onSelect={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_INSURANCE_VERIFICATION_STATUS,
                    value:
                      filterState.insuranceVerificationStatus.concat(value),
                  });
                }}
                options={insuranceVerificationStatusOptions}
                placeholder="Search"
                selected={filterState.insuranceVerificationStatus.map(
                  (val) => ({
                    primaryText: val,
                    value: val,
                  }),
                )}
                title="Insurance status"
                toggleListOnFocus
              />

              <AutoComplete
                endIcon={searchIcon}
                hideAvatar
                id={`${TOP_LEVEL_ID}__episode-levels`}
                isLoading={isGetAdminEpisodeLevelsLoading}
                isTextInput
                marginBottom="16px"
                multiple
                onRemove={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_EPISODE_LEVEL,
                    value: filterState.episodeLevels.filter(
                      (status) => status !== value,
                    ),
                  });
                }}
                onSelect={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_EPISODE_LEVEL,
                    value: filterState.episodeLevels.concat(value),
                  });
                }}
                options={episodeLevelsOptions}
                placeholder="Search"
                selected={filterState.episodeLevels.map((val) => ({
                  primaryText: val,
                  value: val,
                }))}
                title="Level"
                toggleListOnFocus
              />

              <AutoComplete
                endIcon={searchIcon}
                hideAvatar
                id={`${TOP_LEVEL_ID}__episode-treatment-programs`}
                isLoading={isGetEpisodeTreatmentProgramsLoading}
                isTextInput
                marginBottom="16px"
                multiple
                onRemove={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_TREATMENT_PROGRAM,
                    value: filterState.treatmentPrograms.filter(
                      (status) => status !== value,
                    ),
                  });
                }}
                onSelect={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_TREATMENT_PROGRAM,
                    value: filterState.treatmentPrograms.concat(
                      value as EpisodeTreatmentProgram,
                    ),
                  });
                }}
                options={episodeTreatmentProgramsOptions}
                placeholder="Search"
                selected={filterState.treatmentPrograms.map((val) => ({
                  primaryText: val,
                  value: val,
                }))}
                title="Treatment Program"
                toggleListOnFocus
              />

              <AutoComplete
                endIcon={searchIcon}
                hideAvatar
                id={`${TOP_LEVEL_ID}__closed-reason`}
                isLoading={isGetAdminEpisodeStatusReasonsLoading}
                isTextInput
                marginBottom="16px"
                multiple
                onRemove={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_EPISODE_STATUS_REASON,
                    value: filterState.episodeStatusReasons.filter(
                      (status) => status !== value,
                    ),
                  });
                }}
                onSelect={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_EPISODE_STATUS_REASON,
                    value: filterState.episodeStatusReasons.concat(
                      value as EpisodeTreatmentProgram,
                    ),
                  });
                }}
                options={episodeClosedReasonsOption}
                placeholder="Search"
                selected={filterState.episodeStatusReasons.map((val) => ({
                  primaryText: val,
                  value: val,
                }))}
                title="Closed Reason"
                toggleListOnFocus
              />

              <AutoComplete
                endIcon={searchIcon}
                hideAvatar
                id={`${TOP_LEVEL_ID}__psych-clearance-status`}
                isLoading={isGetPsychClearanceStatusesLoading}
                isTextInput
                marginBottom="16px"
                multiple
                onRemove={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_PSYCH_CLEARANCE_STATUSES,
                    value: filterState.psychClearanceStatuses.filter(
                      (status) => status !== value,
                    ),
                  });
                }}
                onSelect={({ value }) => {
                  dispatch({
                    type: FilterActions.UPDATE_PSYCH_CLEARANCE_STATUSES,
                    value: filterState.psychClearanceStatuses.concat(value),
                  });
                }}
                options={psychClearanceStatusesOptions}
                placeholder="Search"
                selected={filterState.psychClearanceStatuses?.map((val) => ({
                  primaryText: val,
                  value: val,
                }))}
                title="Psych clearance status"
                toggleListOnFocus
              />
            </ChakraBox>

            <FireflyButton
              id={`${TOP_LEVEL_ID}__apply`}
              onClick={() => {
                setResetFilter(false);
                onFilterChange(filterState);
                onClose();
              }}
              isFullWidth
            >
              Apply
            </FireflyButton>
          </ChakraMenuList>
        </>
      )}
    </ChakraMenu>
  );
};

export default PatientFilters;
